import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useExportInvestorsMutation, useGetRolesQuery, useGetUsersQuery } from 'api/main';
import { userSelectors } from 'reducers';
import { userSchemas } from 'schemas';
import {
    listingActionButtonStyle,
    ORDER_BY,
    roleCategories,
    RoleNames,
    sortDirection,
    UserEnrichments,
} from 'utils/constants';
import { useDebounce, useLocalStorageFilters, useMatchAtLeastOneRole } from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import InvestorAccountRow from './InvestorAccountRow';
import CustomFilters from '../subscriptions/CustomFilters';
import {
    ActionButton,
    CustomTablePagination,
    EmptyTableBody,
    EnhancedTableHead,
    FiltersButton,
    QuickSearchField,
} from 'components';
import {
    Typography,
    Table,
    Box,
    TableContainer,
    TableBody,
    TableFooter,
    TableRow,
} from '@mui/material';

const tableHeads = [
    {
        id: ORDER_BY.status,
        width: 5,
        isSortable: true,
        label: texts.STATUT,
        align: 'center',
    },
    {
        id: ORDER_BY.lastName,
        width: 15,
        isSortable: true,
        label: texts.NOM,
    },
    {
        id: ORDER_BY.role,
        width: 10,
        isSortable: true,
        label: texts.PROFILE,
    },
    {
        id: ORDER_BY.subscriptionCreatedAt,
        width: 10,
        isSortable: true,
        label: texts.DATE_ENTREE,
    },
    {
        id: texts.SUBSCRIPTIONS,
        width: 40,
        isSortable: false,
        label: texts.SUBSCRIPTIONS,
    },
    {
        id: 'actions',
        width: 5,
        isSortable: false,
        label: '',
    },
];
const filtersKey = 'investorsListFilters';
const defaultEnrichment = [
    UserEnrichments.INVESTOR_PROFILE,
    UserEnrichments.INVESTMENT_FUND,
    UserEnrichments.ROLES,
    UserEnrichments.INVESTMENT_CHARACTERISTICS,
    UserEnrichments.COMPANY,
    UserEnrichments.SUBSCRIPTIONS,
    UserEnrichments.DISTRIBUTOR_COMPANY,
    UserEnrichments.SHARE_SUBCATEGORY,
    UserEnrichments.INTERNAL_RATE_OF_RETURN,
];
const InvestorAccountTable = () => {
    const navigate = useNavigate();
    const internalUser = useSelector(userSelectors.getInternalUser);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const activeImpersonation = useMemo(() => !!internalUser?.impersonatedByUser, [internalUser]);

    const { data: roles = [], isLoading: rolesLoader } = useGetRolesQuery();
    const [exportInvestors, { isLoading: exportLoading }] = useExportInvestorsMutation();
    const investorRoles = useMemo(
        () => roles.filter(r => r.category?.name === roleCategories.INVESTORS),
        [roles]
    );

    const userIsStandardServicer = useMatchAtLeastOneRole([RoleNames.SERVICER_STANDARD]);
    const filteredTableHeads = useMemo(
        () => tableHeads.filter(th => (th.id === 'actions' ? !userIsStandardServicer : true)),
        [userIsStandardServicer]
    );

    const [keyword, setKeyword] = useState('');
    const debouncedKeyword = useDebounce(keyword, 1000);
    const defaultFilters = {
        page: 1,
        orderBy: ORDER_BY.lastName,
        direction: sortDirection.ASC,
        subscriptionStatus: null,
        enrichWith: defaultEnrichment,
        per_page: 100,
        alertTypes: [],
        product: null,
        dist: null,
    };
    const [filters, setFilters] = useLocalStorageFilters(
        filtersKey,
        defaultFilters,
        activeImpersonation || !!debouncedKeyword
    );

    const handleSortUsers = useCallback(
        (property, direction) => {
            navigate('/investor-accounts', { replace: true });
            setFilters(oldValue => ({ ...oldValue, orderBy: property, direction }));
        },
        [navigate, setFilters]
    );
    const handleApplyFilters = useCallback(
        customFilters => {
            setFilters(prevState => ({ ...prevState, ...customFilters, page: 1 }));
        },
        [setFilters]
    );
    const handleToggleFilters = useCallback(() => setFiltersOpen(prevState => !prevState), []);
    const handleKeywordChange = e => {
        setKeyword(e.target.value);
    };

    const {
        data: { userList: investorList, lastPage } = {
            userList: [],
            lastPage: 1,
        },
        isFetching: listLoader,
        refetch,
    } = useGetUsersQuery(
        userSchemas.getUsers({ role: investorRoles.map(role => role.id), ...filters }),
        { skip: !roles.length }
    );

    useEffect(() => {
        if (filters.page > lastPage && lastPage > 0) {
            setFilters(prevState => ({ ...prevState, page: lastPage }));
        }
    }, [lastPage, filters, setFilters]);

    useEffect(() => {
        setFilters(prevState => ({
            ...prevState,
            keyword: debouncedKeyword,
            orderBy: debouncedKeyword ? prevState.orderBy : ORDER_BY.subscriptionId,
            direction: debouncedKeyword ? prevState.direction : sortDirection.DESC,
            page: 1,
            role: investorRoles.map(role => role.id),
        }));
    }, [debouncedKeyword, setFilters, investorRoles]);

    const handlePageChange = useCallback(
        page => () => {
            setFilters(oldValue => ({ ...oldValue, page }));
        },
        [setFilters]
    );

    const handleInvestorsListExport = useCallback(() => {
        const { enrichWith, ...importantFilters } = filters;
        exportInvestors(importantFilters);
    }, [exportInvestors, filters]);

    const dataIsLoading = useMemo(() => rolesLoader || listLoader, [rolesLoader, listLoader]);

    return (
        <React.Fragment>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h5" align="left" gutterBottom>
                    {texts.CLIENT_ACCOUNTS}
                </Typography>
                {!dataIsLoading && (
                    <ActionButton
                        variant="outlined"
                        sx={listingActionButtonStyle}
                        onClick={handleInvestorsListExport}
                        disabled={exportLoading}
                    >
                        {texts.EXPORTER_LISTE}
                    </ActionButton>
                )}
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" marginY={3}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <QuickSearchField
                        value={keyword}
                        onChange={handleKeywordChange}
                        handleClear={() => setKeyword('')}
                    />
                    <FiltersButton onClick={handleToggleFilters} />
                </Box>
            </Box>
            {!dataIsLoading && debouncedKeyword && (
                <Typography variant="body2" gutterBottom>
                    {texts.RESULTS_FOR_KEYWORD(debouncedKeyword)}
                </Typography>
            )}
            <CustomFilters
                ongoing
                open={filtersOpen}
                applyFilters={handleApplyFilters}
                savedFilters={filters}
                loading={listLoader}
                displaySubscriptionStatus
            />
            <TableContainer>
                <Table style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                        {filteredTableHeads.map(c => (
                            <col key={c.id} width={`${c.width}%`} />
                        ))}
                    </colgroup>
                    <EnhancedTableHead
                        headCells={filteredTableHeads}
                        orderBy={filters.orderBy}
                        direction={filters.direction}
                        handleSort={handleSortUsers}
                    />
                    {investorList.length === 0 || dataIsLoading ? (
                        <EmptyTableBody
                            colSpan={filteredTableHeads.length}
                            loading={dataIsLoading}
                            message={
                                debouncedKeyword
                                    ? texts.NO_RESULTS_FOUND
                                    : texts.EMPTY_TABLE_DEFAULT
                            }
                        />
                    ) : (
                        <TableBody>
                            {investorList.map(investor => (
                                <InvestorAccountRow
                                    key={investor.id}
                                    investor={investor}
                                    getInvestors={refetch}
                                />
                            ))}
                        </TableBody>
                    )}
                    <TableFooter>
                        <TableRow>
                            <CustomTablePagination
                                loading={dataIsLoading}
                                currentPage={filters.page}
                                lastPage={lastPage}
                                changePage={handlePageChange}
                                columns={filteredTableHeads.length}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </React.Fragment>
    );
};

export default InvestorAccountTable;
